import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import {
  Spacer,
  Box,
  Modal,
  Flex,
  Button,
  H4,
  TextInput,
} from "orcs-design-system";
import styled from "styled-components";

import { addNewVacantRole as addNewVacantRoleMutation } from "src/allocation/graphql/members.graphql";
import { useAllocationConfig } from "src/contexts/global/WorkspaceContext";
import GroupTypeBadge from "src/components/GroupTypeBadge";
import ErrorNotification from "src/components/ErrorNotification";

const NumberInput = styled(TextInput)`
  width: 100px;
  padding: 5px 8px 6px 8px;
`;

const MAX_VACANCIES = 20;

export const withValueLimit = ({ floatValue }) =>
  floatValue === undefined || (floatValue > 0 && floatValue <= MAX_VACANCIES);

const AddVacantRoleForGroupModal = ({
  group,
  isVisible,
  onHideModal,
  onVacanciesAdded,
  otherGroupIds = [],
}) => {
  const { vacantRoleLabel } = useAllocationConfig();
  const [numberOfVacancies, setNumberOfVacancies] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);

  const [addNewVacantRole] = useMutation(addNewVacantRoleMutation);

  const handleCloseModal = () => {
    setError(null);
    setNumberOfVacancies(1);
    setIsAdding(false);

    onHideModal();
  };

  const handleAddVacantRole = async () => {
    setError(null);
    setIsAdding(true);

    try {
      const people = new Array(numberOfVacancies).fill().map(() => ({
        preferredName: vacantRoleLabel,
        fte: 1,
        directTeamIds: [group.id, ...otherGroupIds],
        isPlaceholder: true,
      }));

      const { data } = await addNewVacantRole({
        variables: {
          input: {
            people,
          },
        },
      });

      await onVacanciesAdded?.(data?.result);

      handleCloseModal();
    } catch (e) {
      setError(e);
    } finally {
      setIsAdding(false);
    }
  };

  const onValueChange = ({ floatValue = null }) => {
    setNumberOfVacancies(floatValue);
  };

  const modalHeader = (
    <Flex alignItems="center">
      <H4 mr="r">{`Add ${vacantRoleLabel} to ${group.name}`}</H4>
      <GroupTypeBadge group={group} />
    </Flex>
  );

  const modalFooter = (
    <Flex>
      <Spacer mr="s">
        <Button
          onClick={handleAddVacantRole}
          variant="success"
          isLoading={isAdding}
          disabled={isAdding || !numberOfVacancies}
        >
          Add
        </Button>
        <Button variant="ghost" onClick={handleCloseModal} disabled={isAdding}>
          Cancel
        </Button>
      </Spacer>
    </Flex>
  );

  return (
    <Modal
      visible={isVisible}
      maxWidth="80vw"
      width="600px"
      height="200px"
      onClose={handleCloseModal}
      footerContent={modalFooter}
      headerContent={modalHeader}
    >
      <Box py="r" px="xs">
        <NumberInput
          height="32px"
          data-testid="role-number-input"
          label={`Specify how many ${vacantRoleLabel}s you'd like to add (maximum 20):`}
          placeholder="1"
          value={numberOfVacancies}
          numberProps={{
            allowNegative: false,
            decimalScale: 0,
            isAllowed: withValueLimit,
            onValueChange,
          }}
        />

        {error && <ErrorNotification error={error} />}
      </Box>
    </Modal>
  );
};

AddVacantRoleForGroupModal.propTypes = {
  group: PropTypes.object.isRequired,
  isVisible: PropTypes.bool,
  onHideModal: PropTypes.func.isRequired,
  onVacanciesAdded: PropTypes.func.isRequired,
  otherGroupIds: PropTypes.arrayOf(PropTypes.string),
};

export default AddVacantRoleForGroupModal;
